import React from 'react';
import styled, { keyframes, css } from 'styled-components';

export interface BarChartData {
  label: string;
  value: number;
  change: number;
}

interface AnalyticsOverviewBarChartProps {
  data: BarChartData[];
  benchmarkData?: Array<{ label: string; value: number | null }>;
  showBenchmarkLegend?: boolean;
  benchmarkVisible?: boolean;
  onBenchmarkToggle?: () => void;
  multiple?: number;
  percent?: boolean;
  barHeight?: string;
  barPadding?: string;
  fontSize?: string;
}

const AnalyticsOverviewBarChart: React.FC<AnalyticsOverviewBarChartProps> = ({
  data,
  benchmarkData = [],
  showBenchmarkLegend = true,
  benchmarkVisible = true,
  onBenchmarkToggle,
  multiple = 1,
  percent = true,
  barHeight = '14px',
  barPadding = '0',
  fontSize = '11px',
}) => {
  const benchmarkMap = new Map(benchmarkData.map(item => [item.label, item.value]));
  const hasBenchmarkData = benchmarkData.some(
    item => typeof item.value === 'number' && Number.isFinite(item.value)
  );

  return (
    <BarContainer>
      {data.map((row, index) => {
        const benchmarkValue = benchmarkMap.get(row.label);
        const hasBenchmarkValue =
          typeof benchmarkValue === 'number' && Number.isFinite(benchmarkValue);
        const benchmarkPercent = hasBenchmarkValue
          ? Math.max(0, Math.min(100, (benchmarkValue as number) * multiple))
          : null;

        return (
          <BarRow key={index} barPadding={barPadding}>
            <TitleAndScore>
              <BarTitle fontSize={fontSize}>{row.label}</BarTitle>
              <ScoreWrapper>
                <Score>
                  {row.value}
                  {percent ? '%' : ''}
                  <Change change={row.change} />
                </Score>
              </ScoreWrapper>
            </TitleAndScore>
            <Bar barHeight={barHeight}>
              <BarFill percentage={row.value * multiple}>
                <span>
                  {row.value}
                  {percent ? '%' : ''}
                </span>
              </BarFill>
              {hasBenchmarkValue && benchmarkPercent !== null && (
                <BenchmarkMarker
                  data-testid={`benchmark-marker-${row.label.replace(/\s+/g, '-')}`}
                  hidden={!benchmarkVisible}
                  style={{ left: `${benchmarkPercent}%` }}
                />
              )}
            </Bar>
          </BarRow>
        );
      })}
      {hasBenchmarkData && showBenchmarkLegend && (
        <BenchmarkLegend
          type="button"
          aria-label="benchmark legend"
          aria-pressed={benchmarkVisible}
          onClick={onBenchmarkToggle}
        >
          <BenchmarkLegendSwatch hidden={!benchmarkVisible} />
          <BenchmarkLegendText>nuLogic Benchmark</BenchmarkLegendText>
        </BenchmarkLegend>
      )}
    </BarContainer>
  );
};
export default AnalyticsOverviewBarChart;

interface ChangeProps {
  change: number;
}

const ChangeWrapper = styled.span<{ change: number }>`
  color: ${({ change }) =>
    change === 0 ? '#3366FF' : change > 0 ? 'green' : change < 0 ? 'red' : undefined};
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 500;
`;

const Change: React.FC<ChangeProps> = ({ change }) => {
  if (change === null || change === undefined) {
    return null;
  }

  return (
    <ChangeWrapper change={change}>{change > 0 ? ` (+${change}%)` : ` (${change}%)`}</ChangeWrapper>
  );
};

const growAnimation = (percentage: number) => keyframes`
  from {
    width: 0%;
  }
  to {
    width: ${percentage}%;
  }
`;

const BarContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  gap: 3px;
  padding-left: 0px;
`;

const TitleAndScore = styled.div`
  flex: 4;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-left: 10px;
`;

const ScoreWrapper = styled.div`
  margin-top: 2px;
  text-align: left;
`;

const BarTitle = styled.div<{ fontSize: string }>`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => props.fontSize};
  color: #333;
  font-weight: 500;
  text-align: left;
`;

const Score = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: #333;
  font-weight: 500;
  padding-left: 8px;
`;

const Bar = styled.div<{ barHeight: string }>`
  position: relative;
  height: ${props => props.barHeight}};
  background-color: #ddd;
  border-radius: 4px;
  overflow: visible;
  flex: 5; 
  cursor: pointer;
  transition: box-shadow 0.1s ease-in-out;

  &:hover {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.3);
  }

  &:hover span {
    opacity: 1;
  }
`;

const BarRow = styled.div<{ barPadding: string }>`
  display: flex;
  align-items: center;
  padding: ${props => props.barPadding}};
`;

const BarFill = styled.div<{ percentage: number }>`
  height: 100%;
  border-radius: 4px;
  background-color: ${props =>
    props.percentage < 40
      ? 'rgba(255, 50, 90, .4)'
      : props.percentage <= 60
        ? 'rgba(255, 216, 111, 1)'
        : 'rgba(129, 199, 132, .7)'};
  animation: ${props => css`
    ${growAnimation(props.percentage)} 0.8s ease-out
  `};
  animation-fill-mode: forwards;
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  span {
    visibility: hidden;
    opacity: 0;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: bold;
    transition: opacity 0.1s ease-in-out;
  }
`;

const BenchmarkMarker = styled.span<{ hidden: boolean }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 0;
  border-left: 2px dashed rgba(230, 101, 121, 0.95);
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  pointer-events: none;
`;

const BenchmarkLegend = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  gap: 8px;
  margin-top: 12px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const BenchmarkLegendSwatch = styled.span<{ hidden: boolean }>`
  width: 22px;
  height: 0;
  border-top: 2px dashed rgba(230, 101, 121, 0.95);
  opacity: ${({ hidden }) => (hidden ? 0.3 : 1)};
`;

const BenchmarkLegendText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #666;
`;
